import { Link, useLocation } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  HeartIcon,
  UserCircleIcon,
  HomeIcon,
  ChatBubbleOvalLeftIcon,
  CalendarIcon as CalendarOutlineIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import {
  MagnifyingGlassIcon as MagnifyingGlassSolidIcon,
  HeartIcon as HeartSolidIcon,
  UserCircleIcon as UserCircleSolidIcon,
  ChatBubbleOvalLeftIcon as ChatBubbleSolidIcon,
  HomeIcon as HomeSolidIcon,
  CalendarIcon as CalendarSolidIcon,
  TagIcon as TagSolidIcon,
} from "@heroicons/react/24/solid";
import { useState, useEffect } from "react";
import { ref, onValue, query, orderByChild } from "../lib/firebase-database"; // Ensure all required functions are imported
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth";

export function MobileBottomNav({ user, activeTab, unreadCount = 0 }) {
  const location = useLocation();
  const path = location.pathname;
  const { user: authUser, isOwner, isAdmin } = useAuth();
  const [realTimeUnreadCount, setRealTimeUnreadCount] = useState(unreadCount || 0);
  const authUid = authUser?.uid;

  // Real-time listener for unread messages
  useEffect(() => {
    if (!authUid) return;

    const chatsRef = ref(db, `chats`);
    const chatsQuery = query(chatsRef, orderByChild("timestamp"));

    const unsubscribe = onValue(chatsQuery, (snapshot) => {
      const chatsData = snapshot.val();
      if (!chatsData) {
        setRealTimeUnreadCount(0);
        return;
      }

      let totalUnread = 0;
      for (const [villaId, villaChats] of Object.entries(chatsData)) {
        if (villaChats[authUid]) {
          const messages = Object.values(villaChats[authUid]);
          const unread = messages.filter(
            (msg) => !msg.read && msg.senderId !== authUid
          ).length;
          totalUnread += unread;
        }
      }
      setRealTimeUnreadCount(totalUnread);
    });

    return () => unsubscribe();
  }, [authUid]);

  // Determine if the user should see the Profile button (owners or admins)
  const showProfileButton = isOwner || isAdmin;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-300 flex justify-around items-center py-2 z-50 h-16">
      <Link to="/" className="flex flex-col items-center">
        {path === "/" ? (
          <>
            <HomeSolidIcon className="h-6 w-6 text-black" />
            <span className="text-xs text-black font-medium">Home</span>
          </>
        ) : (
          <>
            <HomeIcon className="h-6 w-6 text-gray-500" />
            <span className="text-xs text-gray-500">Home</span>
          </>
        )}
      </Link>
      <Link to="/explore" className="flex flex-col items-center">
        {path === "/explore" ? (
          <>
            <MagnifyingGlassSolidIcon className="h-6 w-6 text-black" />
            <span className="text-xs text-black font-medium">Explore</span>
          </>
        ) : (
          <>
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-500" />
            <span className="text-xs text-gray-500">Explore</span>
          </>
        )}
      </Link>
      <Link to="/offers" className="flex flex-col items-center">
        {path === "/offers" ? (
          <>
            <TagSolidIcon className="h-6 w-6 text-black rotate-90" />
            <span className="text-xs text-black font-medium">Offers</span>
          </>
        ) : (
          <>
            <TagIcon className="h-6 w-6 text-gray-500 rotate-90" />
            <span className="text-xs text-gray-500">Offers</span>
          </>
        )}
      </Link>
      {authUser ? (
        <Link to={showProfileButton ? "/profile" : "/profile"} className="flex flex-col items-center">
          {showProfileButton ? (
            // Profile button for owners/admins
            path === "/profile" ? (
              <>
                <UserCircleSolidIcon className="h-6 w-6 text-black" />
                <span className="text-xs text-black font-medium">Profile</span>
              </>
            ) : (
              <>
                <UserCircleIcon className="h-6 w-6 text-gray-500" />
                <span className="text-xs text-gray-500">Profile</span>
              </>
            )
          ) : (
            // Bookings button for regular users
            path === "/profile" ? (
              <>
                <UserCircleIcon className="h-6 w-6 text-black" />
                <span className="text-xs text-black font-medium">Profile</span>
              </>
            ) : (
              <>
                <UserCircleIcon className="h-6 w-6 text-gray-500" />
                <span className="text-xs text-gray-500">Profile</span>
              </>
            )
          )}
        </Link>
      ) : (
        <Link to="/login" className="flex flex-col items-center">
          {path === "/login" ? (
            <>
              <UserCircleSolidIcon className="h-6 w-6 text-black" />
              <span className="text-xs text-black font-medium">Log in</span>
            </>
          ) : (
            <>
              <UserCircleIcon className="h-6 w-6 text-gray-500" />
              <span className="text-xs text-gray-500">Log in</span>
            </>
          )}
        </Link>
      )}
    </nav>
  );
}